import React, {FC} from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import BungalowIcon from '@mui/icons-material/Bungalow';

import UuidSelect from '../common/UuidSelect';

import {SxProps} from '@mui/system/styleFunctionSx/styleFunctionSx';
import {useTranslation} from 'react-i18next';
import {Precinct} from '@/domain/entities/Precinct';
import {Profile} from '@/domain/entities/Profile';
import {Crop, Municipality, Use} from '@/domain/services/Clas';
import {Parcel} from '@/domain/entities/Parcel';

export interface Props {
  isAdmin: boolean,
  crops?: Array<Crop>,
  uses?: Array<Use>,
  municipalities?: Array<Municipality>,
  precinct: Precinct,
  parcel: Parcel,
  owner?: Profile,
  onCropChange: (value: Partial<Precinct>) => void
  onUseChange: (value: Partial<Precinct>) => void
}

const InfoPanel: FC<Props> = ({
  isAdmin,
  crops,
  uses,
  municipalities,
  precinct,
  parcel,
  owner,
  onCropChange,
  onUseChange
}) => {

  const {t} = useTranslation();

  const handleCropChange = (newCrop: string | undefined) => onCropChange({cropUuid: newCrop});
  const handleUseChange = (newUse: string | undefined) => onUseChange({useUuid: newUse});

  const {soilOrganicCarbonTotal, enpName, enpAreaInHa, enpType, rnName, rnAreaInHa, rnType, mabName, mabAreaInHa, cropUuid, useUuid} = precinct;
  const {areaInHa, municipalityUuid, sigpacReference, catastroReference, source} = parcel;

  const municipalityLabel = municipalities?.find(municipality => municipality.uuid === municipalityUuid)?.name || '';

  return <Box sx={sx.container}>
    <Typography variant='overline' sx={{mt: 1}}>{t('precinctInfo')}</Typography>
    <Box sx={sx.userInfo}>
      <Typography variant='h6' sx={sx.user}>{(owner?.first_name || '').toUpperCase()} {(owner?.last_name || '').toUpperCase()}</Typography>
      {isAdmin && <Typography variant="subtitle1" sx={sx.user}>{owner?.nif}</Typography>}
    </Box>
    <Box sx={sx.wrapperData}>
      <BungalowIcon sx={{color: 'grey.700', fontSize: '20px'}}/>
      <Typography variant='body1' sx={sx.municipality}>{municipalityLabel}</Typography>
    </Box>
    <Box sx={sx.wrapperVertical}>
      <Box sx={sx.wrapperHorizontal}>
        <Typography variant='caption' sx={sx.title}>{t('infoPanel.area').toUpperCase()}</Typography>
        <Typography variant='caption' sx={sx.value}>{areaInHa?.toFixed(2)} Ha</Typography>
      </Box>
      <Box sx={sx.wrapperHorizontal}>
        <Typography variant='caption' sx={sx.title}>{t('infoPanel.refcat').toUpperCase()}</Typography>
        <Typography variant='caption' sx={sx.value}>{catastroReference || '-'}</Typography>
      </Box>
      <Box sx={sx.wrapperHorizontal}>
        <Typography variant='caption' sx={sx.title}>{t('infoPanel.sigpac').toUpperCase()}</Typography>
        <Typography variant='caption' sx={sx.value}>{sigpacReference || '-'}</Typography>
      </Box>
      <Box sx={sx.wrapperHorizontal}>
        <Typography variant='caption' sx={sx.title}>{t('infoPanel.soilOrganicCarbon').toUpperCase()}</Typography>
        <Typography variant='caption' sx={sx.value}>{soilOrganicCarbonTotal?.toFixed(4) || '-'} Tn CO<sub>2</sub></Typography>
      </Box>
    </Box>
    <Divider sx={{my: 2}}/>
    {/*PROTECTION____________________________________________________________________________________________________*/}
    
    <Typography variant="caption" sx={sx.subtitle}>{t('infoPanel.protections').toUpperCase()}</Typography>
    
    <Box sx={sx.wrapperHorizontal}>
      <Typography variant='caption' sx={sx.protectionTitle}>{t('ENP')}</Typography>
      <Typography variant='caption' sx={sx.area}>
        {
          enpName
            ? `${enpAreaInHa?.toFixed(2)} Ha`
            : '- Ha'
        }
      </Typography>
      <Box sx={sx.wrapperVertical}>
        <Typography variant='caption' sx={sx.value}>{enpName || '-'}</Typography>
        {!!enpName && <Typography variant="caption" sx={sx.value}>{enpType || ''}</Typography>}
      </Box>
    </Box>
    
    <Box sx={sx.wrapperHorizontal}>
      <Typography variant='caption' sx={sx.protectionTitle}>{t('RN')}</Typography>
      <Typography variant='caption' sx={sx.area}>
        {
          rnName
            ? `${rnAreaInHa?.toFixed(2)} Ha`
            : '- Ha'        
        }
      </Typography>
      <Box sx={sx.wrapperVertical}>
        <Typography variant='caption' sx={sx.value}>{rnName || '-'}</Typography>
        {!!rnName && <Typography variant="caption" sx={sx.value}>{rnType || ''}</Typography>}
      </Box>
    </Box>
    
    <Box sx={sx.wrapperHorizontal}>
      <Typography variant='caption' sx={sx.protectionTitle}>{t('MaB')}</Typography>
      <Typography variant='caption' sx={sx.area}>
        {
          mabName
            ? `${mabAreaInHa?.toFixed(2)} Ha`
            : '- Ha'
        }
      </Typography>
      <Box sx={sx.wrapperVertical}>
        <Typography variant='caption' sx={sx.value}>{mabName || '-'}</Typography>
      </Box>
    </Box>

    <Divider sx={{my: 2}}/>
    
    {/*EDITABLE______________________________________________________________________________________________________*/}
    {
      crops && <Box sx={sx.editableField}>
        <Typography variant='overline' sx={sx.editableFieldTitle}>{t('infoPanel.crop').toUpperCase()}</Typography>
        <UuidSelect items={crops} selectedUuid={cropUuid ?? ''} onChange={handleCropChange}/>
      </Box>
    }
    {
      uses && <Box sx={sx.editableField}>
        <Typography variant='overline' sx={sx.editableFieldTitle}>{t('infoPanel.use').toUpperCase()}</Typography>
        <UuidSelect items={uses} selectedUuid={useUuid ?? ''} onChange={handleUseChange}/>
      
      </Box>
    }
    {/*LEGEND________________________________________________________________________________________________________*/}
    <Divider sx={{my: 2}}/>
    <Box sx={{...sx.wrapperHorizontal, mb: 1}}>
      <Typography variant='caption' component='span' sx={sx.source}>{t('infoPanel.source')}</Typography>
      <Typography variant='caption' component='span' sx={sx.sourceValue}>{source || '-'}</Typography>
    </Box>
    <Box sx={{...sx.wrapperHorizontal, mb: 1}}>
      <Typography variant='caption' component='span' sx={sx.source}>{t('infoPanel.soilOrganicCarbon')}</Typography>
      <Typography variant='caption' component='span' sx={sx.sourceValue}>{t('infoPanel.soilOrganicCarbon_def')}</Typography>
    </Box>
    <Box sx={sx.wrapperHorizontal}>
      <Typography variant='caption' component='span' sx={sx.source}>{t('ENP')}</Typography>
      <Typography variant='caption' component='span' sx={sx.sourceValue}>{t('Espacios Naturales Protegidos')}</Typography>
    </Box>
    <Box sx={sx.wrapperHorizontal}>
      <Typography variant='caption' component='span' sx={sx.source}>{t('RN')}</Typography>
      <Typography variant='caption' component='span' sx={sx.sourceValue}>{t('Red Natura')}</Typography>
    </Box>
    <Box sx={sx.wrapperHorizontal}>
      <Typography variant='caption' component='span' sx={sx.source}>{t('MaB')}</Typography>
      <Typography variant='caption' component='span' sx={sx.sourceValue}>{t('Reserva de la Biosfera')}</Typography>
    </Box>
  </Box>;
};

export default InfoPanel;

const sx: Record<string, SxProps> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 0
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'space-between'
  },
  user: {
    color: 'primary.main',
    fontWeight: 700
  },
  wrapperData: {
    display: 'flex',
    alignItems: 'center',
    mr: 0,
    mb: 1,
  },
  municipality: {
    color: 'grey.700',
    fontWeight: 700,
    mt: 0.25
  },
  area: {
    minWidth: 65,
    color: 'grey.700',
    fontWeight: 700,
  },
  value: {
    color: 'grey.700',
    fontWeight: 700,
  },
  wrapperHorizontalSpaceBetween: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  source: {
    color: 'grey.400',
    fontWeight: 700,
    mr: 1
  },
  sourceValue: {
    fontStyle: 'italic',
    color: 'grey.400',
  },
  subtitle: {
    color: 'primary.main',
    minWidth: 120,
    fontWeight: 700,
    mb: 1
  },
  protectionTitle: {
    color: 'grey.400',
    minWidth: 55,
    fontWeight: 700
  },
  title: {
    color: 'grey.400',
    minWidth: 120,
    fontWeight: 700
  },
  editableFieldTitle: {
    color: 'grey.400',
    minWidth: 70,
    fontWeight: 700
  },
  wrapperVertical: {
    display: 'flex',
    flexDirection: 'column',
  },
  wrapperHorizontal: {
    display: 'flex',
    flexDirection: 'row',
  },
  editableField: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    my: 0.25
  },
  buttonGroup: {
    '&.ButtonGroup-root': {
      maxHeight: 32,
      boxShadow: 0,
      fontSize: 12,
    },
    '& .ButtonGroup-button': {
      minWidth: 50,
      p: 0,
    },
  },
  buttonGroupNoEditable: {
    '&.ButtonGroup-root': {
      maxHeight: 32,
      fontSize: 12,
    },
    '& .ButtonGroup-button': {
      minWidth: 50,
      p: 0,
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    mt: 3,
  },
  button: {
    '&.Mui-disabled': {
      bgcolor: 'grey.300',
      color: 'common.white',
      fontWeight: 800,
    },
  }
};